import React, { Suspense } from 'react';
import { Box, Drawer, IconButton, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { contentBorders } from '../../theme/createContentTheme';

export interface CommandSurfaceProps {
  /** Filters / layer toggles above the map and panel. */
  toolbar?: React.ReactNode;
  /** The map element (usually a LazyZeroMap). */
  map: React.ReactNode;
  /** List shown in the side panel (desktop) or under the map (tablet / phone). */
  panel: React.ReactNode;
  /** Selected item detail; replaces the list on desktop, bottom sheet on phones. */
  detail?: React.ReactNode;
  detailTitle?: React.ReactNode;
  onCloseDetail?: () => void;
  ariaLabel: string;
}

const PANEL_WIDTH = 392;

/**
 * Map-first operational layout. The TomTom map is the primary surface; the
 * panel sits at the inline end (mirrors in Arabic — the map does not).
 */
const CommandSurface: React.FC<CommandSurfaceProps> = ({
  toolbar,
  map,
  panel,
  detail,
  detailTitle,
  onCloseDetail,
  ariaLabel,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const borders = contentBorders(theme);
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const showDetail = Boolean(detail);

  const detailHeader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.25,
        borderBottom: `1px solid ${borders.default}`,
        minHeight: 52,
      }}
    >
      {desktop ? (
        <IconButton
          size="small"
          onClick={onCloseDetail}
          aria-label={t('common.back')}
          sx={{ color: 'text.secondary', '[dir="rtl"] & svg': { transform: 'scaleX(-1)' } }}
        >
          <ArrowBackRounded fontSize="small" />
        </IconButton>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {typeof detailTitle === 'string' ? (
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            {detailTitle}
          </Typography>
        ) : (
          detailTitle
        )}
      </Box>
      {!desktop ? (
        <IconButton size="small" onClick={onCloseDetail} aria-label={t('common.close')} sx={{ color: 'text.secondary' }}>
          <CloseRounded fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );

  return (
    <Box
      component="section"
      aria-label={ariaLabel}
      sx={{
        bgcolor: 'background.paper',
        border: `1px solid ${borders.default}`,
        borderRadius: `${CONTENT_SHAPE.md}px`,
        overflow: 'hidden',
      }}
    >
      {toolbar ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            gap: 1,
            px: { xs: 1.5, md: 2 },
            py: 1.25,
            borderBottom: `1px solid ${borders.default}`,
            overflowX: { xs: 'auto', md: 'visible' },
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {toolbar}
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: `minmax(0, 1fr) ${PANEL_WIDTH}px` },
        }}
      >
        <Box sx={{ position: 'relative', height: { xs: 'clamp(320px, 56vh, 520px)', sm: 440, md: 600 } }}>
          <Suspense fallback={<Skeleton variant="rectangular" sx={{ position: 'absolute', inset: 0, height: '100%' }} />}>
            {map}
          </Suspense>
        </Box>
        <Box
          sx={{
            borderTop: { xs: `1px solid ${borders.default}`, md: 0 },
            borderInlineStart: { md: `1px solid ${borders.default}` },
            height: { md: 600 },
            maxHeight: { xs: 520, md: 'none' },
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          {desktop && showDetail ? (
            <>
              {detailHeader}
              <Box sx={{ flex: 1, overflowY: 'auto', animation: 'zero-fade-in 200ms ease-out' }}>{detail}</Box>
            </>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{panel}</Box>
          )}
        </Box>
      </Box>

      {!desktop ? (
        <Drawer
          anchor="bottom"
          open={showDetail}
          onClose={onCloseDetail}
          PaperProps={{
            sx: {
              maxHeight: phone ? '72vh' : '60vh',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box aria-hidden sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: borders.strong, mx: 'auto', mt: 1 }} />
          {detailHeader}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>{detail}</Box>
        </Drawer>
      ) : null}
    </Box>
  );
};

export default CommandSurface;
